import React, { useEffect, useState } from "react";
import {
  Table,
  TableContainer,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Link,
  Button,
  useDisclosure,
  Text,
  Box,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import dayjs from "dayjs";
import { common } from "../../constants/constants";
import LogModal from "./LogModal";
import _ from "lodash";
import { TABLE_STYLES } from "./AuditTable.styles";
import { getAuditLogById } from "../../services/AuditLog";

export default function AuditTable({
  fetchFiles,
  loading,
  data,
  columns,
  searchText,
  currentPage,
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedLog, setSelectedLog] = useState(null);
  const [loadingLog, setLoadingLog] = useState(false);

  const getRouteFromEndpoint = (row) => {
    if (!row?.end_point) return null;
    const adminIndex = row?.end_point.indexOf("admin/");
    if (adminIndex === -1) return null;

    return row?.action === "CREATE"
      ? row?.end_point.substring(adminIndex + "admin/".length) +
          `/${row?.record_alias}`
      : row?.end_point.substring(adminIndex + "admin/".length); // get everything after 'admin/'
  };

  const handleViewLog = async (row) => {
    if (!row?.id && !row?._id) {
      console.error("No ID found in row data");
      return;
    }

    const logId = row?.id || row?._id;
    setLoadingLog(true);
    setSelectedLog(null);
    onOpen();

    try {
      const detailedLog = await getAuditLogById(logId);
      if (detailedLog?.data) {
        setSelectedLog(detailedLog.data);
      } else {
        setSelectedLog(null);
        console.error("No data received from API");
      }
    } catch (error) {
      console.error("Error fetching audit log details:", error);
      setSelectedLog(null);
    } finally {
      setLoadingLog(false);
    }
  };
  useEffect(() => {
    fetchFiles(currentPage, searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  if (loading) {
    return (
      <TableContainer {...TABLE_STYLES.container}>
        <Table {...TABLE_STYLES.table}>
          <Tbody>
            <Tr>
              <Td colSpan={columns?.length || 3} {...TABLE_STYLES.loadingTd}>
                <Box {...TABLE_STYLES.loadingBox}>
                  <Spinner size="lg" color="blue.500" thickness="3px" />
                  <Text color="gray.500" fontSize="md" fontWeight="500">
                    Loading data...
                  </Text>
                </Box>
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <>
      <TableContainer {...TABLE_STYLES.container}>
        <Table {...TABLE_STYLES.table}>
          <Thead {...TABLE_STYLES.thead}>
            <Tr>
              {columns?.map((col) => (
                <Th key={col?.key} {...TABLE_STYLES.th}>
                  {col?.label}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {data &&
              data?.length > 0 &&
              data?.map((row, rowIndex) => (
                <Tr key={row?.id || rowIndex} {...TABLE_STYLES.tr}>
                  {columns?.map((col) => {
                    const value = row[col?.key];

                    if (col?.key === "changes") {
                      const recordAlias = row?.record_alias || "";
                      const isDisabled = recordAlias.endsWith(".xlsx");
                      return (
                        <Td key={col?.key} {...TABLE_STYLES.td}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewLog(row)}
                            isDisabled={isDisabled}
                          >
                            {common.VIEW}
                          </Button>
                        </Td>
                      );
                    }

                    if (col?.key === "record_alias") {
                      const isXlsxFile =
                        value &&
                        typeof value === "string" &&
                        value.endsWith(".xlsx");
                      const route = getRouteFromEndpoint(row);
                      return (
                        <Td key={col?.key} {...TABLE_STYLES.td}>
                          {isXlsxFile || !route ? (
                            <Text color="gray.500">{value}</Text>
                          ) : (
                            <Link
                              as={RouterLink}
                              color="blue.500"
                              to={`/${route}`}
                            >
                              {value}
                            </Link>
                          )}
                        </Td>
                      );
                    }

                    if (col?.key === "admin_user_id") {
                      return (
                        <Td key={col?.key} {...TABLE_STYLES.td}>
                          {value ? (
                            <Text>{_.capitalize(value?.username)}</Text>
                          ) : (
                            <Text>{`-`}</Text>
                          )}
                        </Td>
                      );
                    }

                    if (col?.key === "entity") {
                      return (
                        <Td key={col?.key} {...TABLE_STYLES.td}>
                          {value ? (
                            <Text>
                              {value
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (c) => c.toUpperCase())}
                            </Text>
                          ) : (
                            <Text>--</Text>
                          )}
                        </Td>
                      );
                    }

                    if (col?.key === "createdAt") {
                      return (
                        <Td key={col?.key} {...TABLE_STYLES.td}>
                          {value
                            ? dayjs(value).format("DD MMM YYYY, hh:mm A")
                            : "--"}
                        </Td>
                      );
                    }

                    return (
                      <Td key={col?.key} {...TABLE_STYLES.td}>
                        {value || "--"}
                      </Td>
                    );
                  })}
                </Tr>
              ))}
          </Tbody>
        </Table>
      </TableContainer>

      <LogModal
        isOpen={isOpen}
        onClose={onClose}
        logData={selectedLog}
        loading={loadingLog}
      />
    </>
  );
}
